import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Typography from "@material-ui/core/Typography"
import Paper from "@material-ui/core/Paper"
import Button from "@material-ui/core/Button"
import Box from "@material-ui/core/Box"
import { makeStyles } from "@material-ui/core/styles"
import NewsletterCta from "./newsletter-cta"
import { trackEvent } from "../lib/analytics"
import {
  ebookPath,
  isPurchasable,
  currentPrice,
  formatPrice,
} from "../lib/ebook"

const useStyles = makeStyles((theme) => ({
  box: {
    padding: theme.spacing(4, 4),
  },
  eyebrow: {
    color: theme.palette.primary.dark,
    letterSpacing: "0.1em",
  },
  heading: {
    marginTop: 0,
  },
}))

const Eyebrow = ({ children }) => {
  const classes = useStyles()
  return (
    <Typography
      variant="overline"
      component="p"
      className={classes.eyebrow}
      gutterBottom
    >
      {children}
    </Typography>
  )
}

// Hero images of every ebook, matched to an ebook by its heroImage path.
const useHeroImage = (ebook) => {
  const { heroes } = useStaticQuery(graphql`
    query EbookHeroImages {
      heroes: allFile(
        filter: { relativeDirectory: { glob: "ebooks/*" }, name: { eq: "hero" } }
      ) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(width: 600, placeholder: BLURRED)
          }
        }
      }
    }
  `)
  const node = heroes.nodes.find((n) => n.relativePath === ebook.heroImage)
  return node && node.childImageSharp.gatsbyImageData
}

const HeroLink = ({ ebook }) => {
  const image = useHeroImage(ebook)
  if (!image) return null
  return (
    <Box mb={2}>
      <a href={ebookPath(ebook)}>
        <GatsbyImage image={image} alt={`The ${ebook.title} box`} />
      </a>
    </Box>
  )
}

const MoreLink = ({ ebook, label }) => (
  <Box mb={1}>
    <Typography variant="body2">
      <a href={ebookPath(ebook)}>{label}</a>
    </Typography>
  </Box>
)

// Promotes an ebook at the end of a recipe or insight. Collects waitlist
// signups until the book is on sale, then links to its sales page.
const EbookCta = ({ ebook, source }) => {
  const classes = useStyles()

  if (!isPurchasable(ebook)) {
    return (
      <NewsletterCta
        heading={`New ebook: ${ebook.title}`}
        body={`${ebook.subtitle}. Launching ${ebook.launch}. Join the waitlist for an early bird price.`}
        buttonLabel="Join the waitlist"
        successLabel="You're on the list"
        listFields={ebook.mailchimpFields}
        onSubscribe={() =>
          trackEvent("generate_lead", { ebook: ebook.slug, source })
        }
      >
        <HeroLink ebook={ebook} />
        <MoreLink ebook={ebook} label="See what's inside" />
      </NewsletterCta>
    )
  }

  return (
    <Paper className={classes.box}>
      <HeroLink ebook={ebook} />
      <Eyebrow>
        {ebook.status === "presale" ? "Early bird pre-order" : "New ebook"}
      </Eyebrow>
      <Typography variant="h4" gutterBottom className={classes.heading}>
        {ebook.title}
      </Typography>
      <Typography variant="body1" gutterBottom>
        {ebook.subtitle}.
      </Typography>
      <MoreLink ebook={ebook} label="See what's inside" />
      <Box mt={2}>
        <Button
          variant="contained"
          size="large"
          color="primary"
          fullWidth
          href={ebookPath(ebook)}
          onClick={() =>
            trackEvent("select_promotion", { ebook: ebook.slug, source })
          }
        >
          Get the ebook · {formatPrice(currentPrice(ebook), ebook.currency)}
        </Button>
      </Box>
    </Paper>
  )
}

export default EbookCta
